import { getApplianceType } from 'src/helpers/device_settings_helpers';
import type { Device } from 'src/types/Device';
import type { ConsumptionStores } from './consumption_helpers';
import type { PvGenerationFlow } from './pv_flow_helpers';
import { hasMonitoringDevices } from './pv_flow_helpers';
import { buildLiveEnergySnapshot } from './live_energy_snapshot';

export type EnergyFlowStatus = {
	key: string;
	default: string;
	tone: 'success' | 'warning' | 'neutral';
};

export type EnergyFlowNodeRole =
	| 'grid'
	| 'generation'
	| 'battery'
	| 'home'
	| 'export'
	| 'room'
	| 'device'
	| 'other';

export type EnergyFlowNode = {
	id: string;
	color?: string;
	/** Optional endpoint image used by compact Sankey presentations. */
	image?: string;
	/** Stable identity used by app logic while `id` remains the chart's display label. */
	identity?: string;
	role?: EnergyFlowNodeRole;
	provenance?: 'measured' | 'calculated';
};

export type EnergySankeyData = {
	unit: string;
	nodes: EnergyFlowNode[];
	links: { source: string; target: string; value: number; color?: string }[];
	status?: EnergyFlowStatus;
	sourceNote?: {
		key: string;
		default: string;
		provenance: 'measured' | 'calculated';
	};
};

/** Derive a flow status from real provider/export power instead of hardcoding it. */
const computeFlowStatus = (grid: number, solar: number, exportPower: number): EnergyFlowStatus => {
	const incoming = grid + solar;
	if (incoming <= 0) {
		return {
			key: 'energy_monitoring_flow_status_idle',
			default: 'No active flow',
			tone: 'neutral',
		};
	}
	if (exportPower > 0 && exportPower >= grid) {
		return {
			key: 'energy_monitoring_flow_status_exporting',
			default: 'Exporting surplus',
			tone: 'success',
		};
	}
	const solarShare = solar / incoming;
	if (solarShare >= 0.6) {
		return {
			key: 'energy_monitoring_flow_status_solar',
			default: 'Solar-led',
			tone: 'success',
		};
	}
	if (solarShare <= 0.25) {
		return {
			key: 'energy_monitoring_flow_status_grid',
			default: 'Grid-led',
			tone: 'warning',
		};
	}
	return {
		key: 'energy_monitoring_flow_status_balanced',
		default: 'Balanced mix',
		tone: 'neutral',
	};
};

export type EnergyFlowVisibility = {
	showSupply?: boolean;
	/** @deprecated Use `showSupply`; retained for persisted widget compatibility. */
	showGeneration?: boolean;
	showRooms: boolean;
	showDevices: boolean;
};

export type EnergyFlowSupplyMode = 'auto' | 'measured' | 'calculated';

export type EnergyFlowLimits = {
	maxRooms: number;
	maxDevicesPerRoom: number;
	maxFlatDevices: number;
};

export const DEFAULT_FLOW_LIMITS: EnergyFlowLimits = {
	maxRooms: 6,
	maxDevicesPerRoom: 4,
	maxFlatDevices: 8,
};

export const COMPACT_FLOW_LIMITS: EnergyFlowLimits = {
	maxRooms: 3,
	maxDevicesPerRoom: 2,
	maxFlatDevices: 4,
};

export const FLOW_NODE_COLORS = {
	grid: 'var(--color-primary)',
	solar: 'var(--color-chart-generated-energy)',
	battery: 'var(--color-success)',
	home: 'var(--color-lightblue)',
	export: 'var(--color-muted)',
	room: 'var(--color-orange)',
	consumer: 'var(--color-ventilation)',
} as const;

const DEMO_FLOW_NODES = [
	['grid', 'energy_monitoring_flow_grid_supply', 'Grid supply', FLOW_NODE_COLORS.grid, 'grid'],
	['solar', 'energy_monitoring_kpi_solar', 'Solar roof', FLOW_NODE_COLORS.solar, 'generation'],
	['battery', 'icon_name_battery', 'Battery', FLOW_NODE_COLORS.battery, 'battery'],
	['home', 'label_home', 'Home', FLOW_NODE_COLORS.home, 'home'],
	[
		'heating',
		'energy_monitoring_flow_demo_heating',
		'Heating & cooling',
		'var(--color-danger)',
		'device',
	],
	['ev', 'energy_monitoring_flow_demo_ev', 'EV charger', 'var(--color-success)', 'device'],
	['kitchen', 'energy_monitoring_flow_demo_kitchen', 'Kitchen', 'var(--color-orange)', 'device'],
	['laundry', 'energy_monitoring_flow_demo_laundry', 'Laundry', 'var(--color-purple)', 'device'],
	['lighting', 'appliance_type_lighting', 'Lighting', 'var(--color-yellow)', 'device'],
	['office', 'energy_monitoring_flow_demo_office', 'Home office', FLOW_NODE_COLORS.consumer, 'device'],
	['export', 'energy_monitoring_flow_demo_export', 'Export', FLOW_NODE_COLORS.export, 'export'],
	['other', 'energy_monitoring_flow_demo_other', 'Other load', FLOW_NODE_COLORS.consumer, 'other'],
] as const satisfies readonly (readonly [string, string, string, string, EnergyFlowNodeRole])[];

const DEMO_FLOW_LINKS = [
	['grid', 'home', 3360, FLOW_NODE_COLORS.grid],
	['solar', 'home', 2140, FLOW_NODE_COLORS.solar],
	['battery', 'home', 920, FLOW_NODE_COLORS.battery],
	['solar', 'export', 260, FLOW_NODE_COLORS.export],
	['home', 'heating', 2540, 'var(--color-danger)'],
	['home', 'ev', 1920, 'var(--color-success)'],
	['home', 'kitchen', 840, 'var(--color-orange)'],
	['home', 'laundry', 620, 'var(--color-purple)'],
	['home', 'lighting', 286, 'var(--color-yellow)'],
	['home', 'office', 214, FLOW_NODE_COLORS.consumer],
] as const;

export const buildDemoEnergyFlow = (
	formatLabel: (key: string, fallback: string) => string,
	supplyMode: EnergyFlowSupplyMode = 'auto',
): EnergySankeyData => {
	const calculatedSupply = supplyMode === 'calculated';
	const labels = new Map(
		DEMO_FLOW_NODES.map(([identity, key, fallback]) => [identity, formatLabel(key, fallback)]),
	);
	return {
		unit: 'W',
		nodes: DEMO_FLOW_NODES.map(([identity, , , color, role]) => ({
			id: labels.get(identity) as string,
			identity: `demo:${identity}`,
			role,
			color,
			...(identity === 'grid'
				? { provenance: calculatedSupply ? ('calculated' as const) : ('measured' as const) }
				: {}),
		})),
		links: DEMO_FLOW_LINKS.map(([source, target, value, color]) => ({
			source: labels.get(source) as string,
			target: labels.get(target) as string,
			value,
			color,
		})),
		sourceNote: {
			key: calculatedSupply
				? 'energy_monitoring_flow_supply_note_calculated'
				: 'energy_monitoring_flow_supply_note_measured',
			default: calculatedSupply
				? 'Grid supply is calculated from live room and device consumption.'
				: 'Grid supply is measured at the home entry point.',
			provenance: calculatedSupply ? 'calculated' : 'measured',
		},
	};
};

export const getEnergyChartEmptyMessage = (
	devices: Record<string, Device>,
	formatLabel: (key: string, fallback: string) => string,
): string => {
	if (!hasMonitoringDevices(devices)) {
		return formatLabel(
			'energy_monitoring_state_power_meter_setup',
			'Add a power meter to see energy data.',
		);
	}
	return formatLabel('energy_monitoring_state_no_activity_message', 'No activity to display.');
};

const applianceColor = (device: Device | null | undefined) => {
	const type = device ? getApplianceType(device) : null;
	return type?.chartColor ?? FLOW_NODE_COLORS.consumer;
};

const addLink = (
	links: EnergySankeyData['links'],
	source: string,
	target: string,
	value: number,
	color?: string,
) => {
	if (value <= 0) return;
	links.push({ source, target, value, color });
};

const ensureNode = (
	nodes: EnergySankeyData['nodes'],
	id: string,
	color?: string,
	metadata?: Pick<EnergyFlowNode, 'identity' | 'role'>,
) => {
	if (!nodes.some((n) => n.id === id)) {
		nodes.push({ id, color, ...metadata });
	}
};

const finalizeSankey = (data: EnergySankeyData): EnergySankeyData | null => {
	const links = data.links.filter(
		(l) => Number.isFinite(l.value) && l.value > 0 && l.source !== l.target,
	);
	if (!links.length) return null;

	const referenced = new Set<string>();
	links.forEach((l) => {
		referenced.add(l.source);
		referenced.add(l.target);
	});

	const nodeById = new Map(data.nodes.map((n) => [n.id, n]));
	referenced.forEach((id) => {
		if (!nodeById.has(id)) nodeById.set(id, { id });
	});

	const nodes = [...nodeById.values()].filter((n) => referenced.has(n.id));
	return nodes.length && links.length ? { ...data, nodes, links } : null;
};

const isSupplyVisible = (visibility: EnergyFlowVisibility) =>
	visibility.showSupply ?? visibility.showGeneration ?? true;

const uniqueDeviceNodeId = (title: string, deviceId: string, used: Set<string>) => {
	let id = title.trim() || deviceId;
	if (used.has(id)) id = `${id} (${deviceId})`;
	used.add(id);
	return id;
};

export const buildEnergyFlowSankey = (
	stores: ConsumptionStores,
	devices: Record<string, Device>,
	rooms: Record<string | number, { id: number; name: string }>,
	formatLabel: (key: string, fallback: string) => string,
	visibility: EnergyFlowVisibility,
	limits: EnergyFlowLimits = DEFAULT_FLOW_LIMITS,
	pvFlow: PvGenerationFlow | null = null,
	supplyMode: EnergyFlowSupplyMode = 'auto',
	homeName = '',
): EnergySankeyData | null => {
	const live = buildLiveEnergySnapshot(stores, devices, pvFlow);
	const gridPower = live.gridImportW;
	const solarPower = live.solarW;
	const exportPower = live.exportW;
	const solarToHome = live.solarToHomeW;
	const homeConsumption = live.loadW;

	const devicesByRoom: Record<number, { id: string; title: string; power: number; color: string }[]> =
		{};
	const unassignedDevices: {
		id: string;
		title: string;
		power: number;
		color: string;
	}[] = [];

	const identifiedLoadW = live.consumers.reduce((sum, consumer) => sum + consumer.power, 0);
	// A parent meter and its children may both be present in the live store. Keep
	// the visual allocation inside the authoritative home boundary instead of
	// allowing those overlapping measurements to inflate the Sankey total.
	const allocationScale =
		homeConsumption > 0 && identifiedLoadW > homeConsumption ? homeConsumption / identifiedLoadW : 1;

	live.consumers.forEach((consumer) => {
		const deviceID = consumer.id;
		const device = devices[deviceID];
		if (!device) return;
		const power = consumer.power * allocationScale;
		if (power <= 0) return;

		const row = {
			id: deviceID,
			title: consumer.title,
			power,
			color: applianceColor(device),
		};

		const roomId = consumer.roomId;
		if (roomId != null) {
			if (!devicesByRoom[roomId]) devicesByRoom[roomId] = [];
			devicesByRoom[roomId].push(row);
		} else {
			unassignedDevices.push(row);
		}
	});

	const roomPowerById = new Map<number, { id: number; title: string; power: number }>();
	Object.entries(devicesByRoom).forEach(([roomId, roomDevices]) => {
		const id = Number(roomId);
		const deviceTotal = roomDevices.reduce((sum, device) => sum + device.power, 0);
		roomPowerById.set(id, {
			id,
			title: rooms[id]?.name ?? `Room ${id}`,
			power: deviceTotal,
		});
	});
	const representedConsumerLoadW = identifiedLoadW * allocationScale;
	const unknownLoadW = Math.max(homeConsumption - representedConsumerLoadW, 0);
	if (unknownLoadW > 0) {
		unassignedDevices.push({
			id: 'unknown',
			title: formatLabel('appliance_type_unknown', 'Unknown'),
			power: unknownLoadW,
			color: FLOW_NODE_COLORS.consumer,
		});
	}
	const roomsPower: { id: number; title: string; power: number }[] = [...roomPowerById.values()]
		.filter((room) => room.power > 0)
		.sort((a, b) => b.power - a.power);

	const homeLabel = homeName.trim() || formatLabel('label_home', 'Home');
	const gridLabel = formatLabel('energy_monitoring_flow_grid_supply', 'Grid supply');
	const solarLabel = formatLabel('label_solar_roof', 'Solar');
	const exportLabel = formatLabel('label_export', 'Export');

	const nodes: EnergySankeyData['nodes'] = [];
	const links: EnergySankeyData['links'] = [];

	const showSupply = isSupplyVisible(visibility);
	const roomLoadW = roomsPower.reduce((sum, room) => sum + room.power, 0);
	const deviceLoadW = [...Object.values(devicesByRoom).flat(), ...unassignedDevices].reduce(
		(sum, device) => sum + device.power,
		0,
	);
	const inferredHomeLoadW = Math.max(homeConsumption, roomLoadW, deviceLoadW);
	const calculatedGridPower = Math.max(inferredHomeLoadW - solarToHome, 0);
	const useCalculatedGrid =
		supplyMode === 'calculated' ||
		(supplyMode === 'auto' && gridPower <= 0 && calculatedGridPower > 0);
	const effectiveGridPower = useCalculatedGrid ? calculatedGridPower : gridPower;
	const hasGeneration = showSupply && solarPower > 0;
	const hasGrid = showSupply && effectiveGridPower > 0;
	const hasExport = showSupply && exportPower > 0;
	const hasRooms = visibility.showRooms && roomsPower.length > 0;
	const hasDevices =
		visibility.showDevices &&
		(Object.keys(devicesByRoom).length > 0 || unassignedDevices.length > 0);

	if (!hasGrid && !hasGeneration && !hasRooms && !hasDevices) {
		return null;
	}

	ensureNode(nodes, homeLabel, FLOW_NODE_COLORS.home, {
		identity: 'home',
		role: 'home',
	});

	if (hasGrid) {
		ensureNode(nodes, gridLabel, FLOW_NODE_COLORS.grid, {
			identity: 'grid',
			role: 'grid',
		});
		const gridNode = nodes.find((node) => node.id === gridLabel);
		if (gridNode) gridNode.provenance = useCalculatedGrid ? 'calculated' : 'measured';
		addLink(links, gridLabel, homeLabel, effectiveGridPower, FLOW_NODE_COLORS.grid);
	}

	if (hasGeneration) {
		ensureNode(nodes, solarLabel, FLOW_NODE_COLORS.solar, {
			identity: 'solar',
			role: 'generation',
		});
		const toHome = pvFlow?.hasPvSetup ? solarToHome : Math.max(solarPower - exportPower, 0);
		if (toHome > 0) {
			addLink(links, solarLabel, homeLabel, toHome, FLOW_NODE_COLORS.solar);
		}
	}

	if (hasExport) {
		ensureNode(nodes, exportLabel, FLOW_NODE_COLORS.export, {
			identity: 'export',
			role: 'export',
		});
		const exportSource = solarPower > 0 ? solarLabel : gridLabel;
		if (exportSource === gridLabel) {
			ensureNode(nodes, gridLabel, FLOW_NODE_COLORS.grid, {
				identity: 'grid',
				role: 'grid',
			});
		}
		addLink(links, exportSource, exportLabel, exportPower, FLOW_NODE_COLORS.export);
	}

	if (hasDevices && hasRooms) {
		const usedDeviceIds = new Set<string>();
		const visibleRooms = roomsPower.slice(0, limits.maxRooms);
		visibleRooms.forEach((room) => {
			const roomTitle = rooms[room.id]?.name ?? room.title;
			ensureNode(nodes, roomTitle, FLOW_NODE_COLORS.room, {
				identity: `room:${room.id}`,
				role: 'room',
			});

			const allRoomDevices = [...(devicesByRoom[room.id] ?? [])].sort((a, b) => b.power - a.power);
			const roomDevices = allRoomDevices.slice(0, limits.maxDevicesPerRoom);
			const allDeviceTotal = allRoomDevices.reduce((sum, device) => sum + device.power, 0);
			const visibleDeviceTotal = roomDevices.reduce((sum, device) => sum + device.power, 0);
			const homeToRoom = Math.max(room.power, allDeviceTotal);
			addLink(links, homeLabel, roomTitle, homeToRoom, FLOW_NODE_COLORS.home);

			roomDevices.forEach((device) => {
				const deviceNodeId = uniqueDeviceNodeId(device.title, device.id, usedDeviceIds);
				ensureNode(nodes, deviceNodeId, device.color, {
					identity: `device:${device.id}`,
					role: 'device',
				});
				addLink(links, roomTitle, deviceNodeId, device.power, device.color);
			});

			const otherDevicePower = Math.max(homeToRoom - visibleDeviceTotal, 0);
			if (otherDevicePower > 0) {
				const otherDevicesLabel = `${roomTitle} · ${formatLabel(
					'energy_monitoring_flow_other_devices',
					'Other devices',
				)}`;
				ensureNode(nodes, otherDevicesLabel, FLOW_NODE_COLORS.consumer, {
					identity: `room:${room.id}:other`,
					role: 'other',
				});
				addLink(
					links,
					roomTitle,
					otherDevicesLabel,
					otherDevicePower,
					FLOW_NODE_COLORS.consumer,
				);
			}
		});

		const hiddenRoomsPower = roomsPower
			.slice(visibleRooms.length)
			.reduce((sum, room) => sum + room.power, 0);
		if (hiddenRoomsPower > 0) {
			const otherRoomsLabel = formatLabel('energy_monitoring_flow_other_rooms', 'Other rooms');
			ensureNode(nodes, otherRoomsLabel, FLOW_NODE_COLORS.room, {
				identity: 'rooms:other',
				role: 'other',
			});
			addLink(links, homeLabel, otherRoomsLabel, hiddenRoomsPower, FLOW_NODE_COLORS.room);
		}

		unassignedDevices
			.sort((a, b) => b.power - a.power)
			.slice(0, limits.maxDevicesPerRoom)
			.forEach((device) => {
				const deviceNodeId = uniqueDeviceNodeId(device.title, device.id, usedDeviceIds);
				ensureNode(nodes, deviceNodeId, device.color, {
					identity: `device:${device.id}`,
					role: 'device',
				});
				addLink(links, homeLabel, deviceNodeId, device.power, device.color);
			});
	} else if (hasDevices) {
		const usedDeviceIds = new Set<string>();
		const allFlatDevices = [...Object.values(devicesByRoom).flat(), ...unassignedDevices].sort(
			(a, b) => b.power - a.power,
		);
		const flatDevices = allFlatDevices.slice(0, limits.maxFlatDevices);

		flatDevices.forEach((device) => {
			const deviceNodeId = uniqueDeviceNodeId(device.title, device.id, usedDeviceIds);
			ensureNode(nodes, deviceNodeId, device.color, {
				identity: `device:${device.id}`,
				role: 'device',
			});
			addLink(links, homeLabel, deviceNodeId, device.power, device.color);
		});
		const hiddenDevicePower = allFlatDevices
			.slice(flatDevices.length)
			.reduce((sum, device) => sum + device.power, 0);
		if (hiddenDevicePower > 0) {
			const otherDevicesLabel = formatLabel(
				'energy_monitoring_flow_other_devices',
				'Other devices',
			);
			ensureNode(nodes, otherDevicesLabel, FLOW_NODE_COLORS.consumer, {
				identity: 'devices:other',
				role: 'other',
			});
			addLink(links, homeLabel, otherDevicesLabel, hiddenDevicePower, FLOW_NODE_COLORS.consumer);
		}
	} else if (hasRooms) {
		const visibleRooms = roomsPower.slice(0, limits.maxRooms);
		visibleRooms.forEach((room) => {
			const roomTitle = rooms[room.id]?.name ?? room.title;
			ensureNode(nodes, roomTitle, FLOW_NODE_COLORS.room, {
				identity: `room:${room.id}`,
				role: 'room',
			});
			addLink(links, homeLabel, roomTitle, room.power, FLOW_NODE_COLORS.room);
		});
		const hiddenRoomsPower = roomsPower
			.slice(visibleRooms.length)
			.reduce((sum, room) => sum + room.power, 0);
		if (hiddenRoomsPower > 0) {
			const otherRoomsLabel = formatLabel('energy_monitoring_flow_other_rooms', 'Other rooms');
			ensureNode(nodes, otherRoomsLabel, FLOW_NODE_COLORS.room, {
				identity: 'rooms:other',
				role: 'other',
			});
			addLink(links, homeLabel, otherRoomsLabel, hiddenRoomsPower, FLOW_NODE_COLORS.room);
		}
	}

	const modeledHomeLoad = Math.max(
		inferredHomeLoadW,
		effectiveGridPower + solarToHome - exportPower,
		0,
	);
	const representedHomeLoad = links
		.filter((link) => link.source === homeLabel && link.target !== exportLabel)
		.reduce((sum, link) => sum + link.value, 0);
	const residual = Math.max(modeledHomeLoad - representedHomeLoad, 0);
	if (residual > 0) {
		const otherLabel = formatLabel('label_other_load', 'Other load');
		ensureNode(nodes, otherLabel, FLOW_NODE_COLORS.consumer, {
			identity: 'other',
			role: 'other',
		});
		addLink(links, homeLabel, otherLabel, residual, FLOW_NODE_COLORS.home);
	}

	const status = computeFlowStatus(effectiveGridPower, solarPower, exportPower);
	const sourceNote = hasGrid
		? useCalculatedGrid
			? {
					key: 'energy_monitoring_flow_supply_note_calculated',
					default: 'Grid supply is calculated from live room and device consumption.',
					provenance: 'calculated' as const,
				}
			: {
					key: 'energy_monitoring_flow_supply_note_measured',
					default: 'Grid supply is measured at the home entry point.',
					provenance: 'measured' as const,
				}
		: undefined;
	const result = finalizeSankey({ nodes, links, unit: 'W', status, sourceNote });
	return result;
};

export const applyEnergyFlowVisibility = (
	data: EnergySankeyData,
	visibility: EnergyFlowVisibility,
): EnergySankeyData | null => {
	const { showRooms, showDevices } = visibility;
	const showSupply = isSupplyVisible(visibility);
	const idsForRole = (role: EnergyFlowNodeRole) =>
		new Set(data.nodes.filter((node) => node.role === role).map((node) => node.id));
	const homeIds = idsForRole('home');
	const gridSources = idsForRole('grid');
	const generationSources = new Set([...idsForRole('generation'), ...idsForRole('battery')]);
	const exportIds = idsForRole('export');
	const roomIds = idsForRole('room');
	const deviceIds = idsForRole('device');
	const otherNode = data.nodes.find((node) => node.role === 'other');

	// Backward compatibility for callers that still provide nodes without role metadata.
	if (!homeIds.size) homeIds.add('Home');
	if (!gridSources.size) {
		['Grid', 'Grid supply'].forEach((id) => gridSources.add(id));
	}
	if (!generationSources.size) {
		['Solar roof', 'Solar', 'Battery'].forEach((id) => generationSources.add(id));
	}
	if (!exportIds.size) exportIds.add('Export');
	if (!roomIds.size && data.nodes.every((node) => !node.role)) {
		data.links
			.filter((link) => homeIds.has(link.source))
			.map((link) => link.target)
			.filter((target) => data.links.some((link) => link.source === target))
			.forEach((id) => roomIds.add(id));
	}

	let links = data.links.filter((link) => {
		if (!showSupply) {
			if (gridSources.has(link.source)) return false;
			if (generationSources.has(link.source)) return false;
			if (exportIds.has(link.source) || exportIds.has(link.target)) return false;
		}
		return true;
	});

	if (!showDevices) {
		links = links.filter((link) => !roomIds.has(link.source) && !deviceIds.has(link.target));
	}

	if (!showRooms) {
		const aggregateValue = links
			.filter((l) => homeIds.has(l.source) && !exportIds.has(l.target))
			.reduce((sum, l) => sum + l.value, 0);

		links = links.filter((l) => {
			if (homeIds.has(l.source) && roomIds.has(l.target)) return false;
			if (roomIds.has(l.source)) return false;
			return true;
		});

		if (aggregateValue > 0 && showDevices) {
			const deviceLinks = data.links.filter((l) => roomIds.has(l.source));
			deviceLinks.forEach((l) => {
				addLink(links, [...homeIds][0], l.target, l.value, l.color);
			});
		} else if (aggregateValue > 0) {
			addLink(
				links,
				[...homeIds][0],
				otherNode?.id ?? 'Other load',
				aggregateValue,
				FLOW_NODE_COLORS.consumer,
			);
		}
	}

	const referenced = new Set<string>();
	links.forEach((l) => {
		referenced.add(l.source);
		referenced.add(l.target);
	});

	const nodes = data.nodes.filter((n) => referenced.has(n.id));
	const otherId = otherNode?.id ?? 'Other load';
	if (!nodes.some((node) => node.id === otherId) && links.some((link) => link.target === otherId)) {
		nodes.push(
			otherNode ?? {
				id: otherId,
				identity: 'other',
				role: 'other',
				color: FLOW_NODE_COLORS.consumer,
			},
		);
	}

	return finalizeSankey({
		...data,
		nodes,
		links,
		sourceNote: showSupply ? data.sourceNote : undefined,
	});
};

export const buildApplianceBreakdown = (stores: ConsumptionStores, devices: Record<string, Device>) => {
	const sorted = Object.entries(buildLiveEnergySnapshot(stores, devices).byAppliance).sort(
		([, a], [, b]) => b - a,
	);
	return {
		keys: sorted.map(([key]) => key),
		values: sorted.map(([, value]) => value),
		total: sorted.reduce((sum, [, value]) => sum + value, 0),
	};
};
